import styled from "styled-components";
import React from "react";
import { SourceProps, SourceType } from "Data/detail";
import Dim from "Components/Dim";
import SourceImage from "Components/SourceImage";
import SourceVideo from "Components/SourceVideo";
import { getYoutubeThumbnail } from "../../util";
import BtnIcon from "Components/Btn/BtnIcon";
import media, { BREAKPOINT_TABLET } from "Styles/media";
import useWindowSize from "Hooks/useWindowSize";
import useSliderCount from "Hooks/useSliderCount";

const Wrapper = styled.div``;

const Inner = styled.div`
	position: fixed;
	z-index: ${(props) => props.theme.zIndex.popup};
	width: ${(props) => `calc(100% - ${props.theme.size.offset.pc * 4}px - ${props.theme.size.logo.pc * 2}px);`};
	max-width: 600px;

	height: ${(props) => props.theme.layout.full_height_layout};
	left: 50%;
	transform: translateX(-50%);
	top: 0;
	animation: fadeIn 1s ease-in-out;
	display: flex;
	flex-direction: column;

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	@media ${media.tablet} {
		max-width: initial;
		width: 100%;
		top: 0;
		padding-top: ${(props) => `${props.theme.size.logo.tablet + props.theme.size.offset.tablet * 2}px`};
	}
`;

const ViewWrapper = styled.div`
	flex-grow: 1;
	display: flex;
	${(props) => props.theme.layout.center_flex};
`;

const Viewer = styled.div`
	border: 1px solid white;
	position: relative;
	width: 100%;
	background-color: ${(props) => props.theme.color.bg};
`;

const PreviewWrapper = styled.div`
	position: relative;
`;

const Preview = styled.ul`
	display: flex;
	overflow: scroll;
	scroll-behavior: smooth;
	${(props) => props.theme.layout.scroll_hide};

	&:after {
		content: "";
		width: 8px;
		border: 1px solid transparent;
		display: block;
	}
`;

const List = styled.li<{ active: boolean }>`
	position: relative;
	${(props) => props.theme.layout.ratio(56.24)};
	cursor: pointer;
	width: calc((100% - 12px * 6 * 2) / 6);
	flex-grow: 0;
	flex-shrink: 0;
	border: ${(props) => props.active && "1px solid white"};
	opacity: ${(props) => (props.active ? 1 : 0.5)};
	margin: 12px;

	@media ${media.mobile} {
		margin: 6px;
		width: calc((100% - 6px * 6 * 2) / 6);
	}

	@media ${media.hover} {
		&:hover {
			border: 1px solid white;
			opacity: 1;
		}
	}
`;

const Image = styled(SourceImage)`
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
`;

const BtnClose = styled(BtnIcon)`
	position: absolute;
	right: 0;
	top: 0;
	transform: translateY(-120%);

	@media ${media.tablet} {
		right: ${(props) => `${props.theme.size.offset.tablet}px`};
		top: ${(props) => `${props.theme.size.offset.tablet}px`};
		transform: initial;
	}
`;

const BtnWrapper = styled.div`
	@media ${media.touch} {
		display: none;
	}
`;
const BtnDefault = styled(BtnIcon)`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	z-index: 5;
`;
const BtnLeft = styled(BtnDefault)`
	left: 0;
	transform: translateY(-50%) translateX(-100%);
`;
const BtnRight = styled(BtnDefault)`
	right: 0;
	transform: translateY(-50%) translateX(100%);
`;

const GalleryViewer: React.FC<{ data: SourceProps[]; nowData: SourceProps | undefined; handleCloseClick: () => void; handleListClick: (id: string) => void }> = ({
	data,
	nowData,
	handleCloseClick,
	handleListClick
}) => {
	const { size } = useWindowSize();
	const { slider, slideDown, slideUp } = useSliderCount();
	return (
		<Wrapper>
			<Dim active={!!nowData} />
			{nowData && (
				<Inner>
					{size.width <= BREAKPOINT_TABLET && <BtnClose icon={"close"} onClick={handleCloseClick} />}
					<ViewWrapper>
						<Viewer>
							{size.width > BREAKPOINT_TABLET && <BtnClose icon={"close"} onClick={handleCloseClick} />}

							{nowData.type === SourceType.IMAGE ? <SourceImage image={nowData.value} /> : <SourceVideo source={nowData.value} />}
						</Viewer>
					</ViewWrapper>

					<PreviewWrapper>
						<Preview ref={slider}>
							{data.map((el) => {
								return (
									<List key={el.id} onClick={() => handleListClick(el.id)} active={el.id === nowData.id}>
										<Image image={el.type === SourceType.IMAGE ? el.value : getYoutubeThumbnail(el.value)} />
									</List>
								);
							})}
						</Preview>
						<BtnWrapper>
							<BtnLeft icon={"left"} onClick={slideDown} />
							<BtnRight icon={"right"} onClick={slideUp} />
						</BtnWrapper>
					</PreviewWrapper>
				</Inner>
			)}
		</Wrapper>
	);
};

export default GalleryViewer;
